import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { round, template } from 'lodash'
import { hasValue } from '../../../utils/NumberUtil'
import { DESCRIPTION } from '../../../home/constants/RouteConstants'
import { CAMPAIGNS, EVENTS } from '../../../alerting/constants/AlertConstants'
import { getFullDateMini } from '../../../utils/DateUtil'
import DtoStation from '../../../station/dto/DtoStation'
import { PIEZOMETER_SPI_TRENDS } from '../../constants/piezo/PiezometerStationConstants'
import { getLabel } from '../../../utils/StoreUtils'
import { getI18nOrLabel } from '../../../utils/StringUtil'
import { Grid, Stack, Tooltip, Typography } from '@mui/material'
import { Event, Grade, Help, Info, NaturePeople, Notifications, Wifi } from '@mui/icons-material'
import { getSiteUrl } from 'utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION } from 'station/constants/StationConstants'

const StationMapSituationCard = ({
    station = {},
    onClick = () => {},
    onHover = () => {},
    stabilityPercent,
    valueLabel,
}) => {
    const stabilityTooltipContent = (
        <Stack>
            <Typography>{`Seuil de stabilité : ${station.stabilityGap}`}</Typography>
            <Typography>{`${station.indicatorValue} - ${station.stabilityMeasureValue} = ${round(station.indicatorValue - station.stabilityMeasureValue, 3)} => Tendance ${getLabel(PIEZOMETER_SPI_TRENDS, station.trendLabel, 'label', 'imgLabel')}`}</Typography>
            <Typography>{`Ce seuil est calculé à partir de ${stabilityPercent ?? 10}% / 2 de l'écart entre le min et le max historique.`}</Typography>
        </Stack>
    )
    return (
        <Stack
            onClick={() => onClick(station, DESCRIPTION)}
            onMouseOver={() => onHover(station, true)}
            onMouseOut={() => onHover(station, false)}
        >
            <Grid container columnSpacing={1} sx={{ alignItems: 'center', padding: '0 8px' }}>
                <Grid size={'auto'}>
                    {
                        station.markerIcon ?
                            station.markerIcon?.startsWith('<svg') ? (
                                <img style={{ width: '26px', height: '26px', left: 'auto', top: 'auto', margin: '10' }} src={`data:image/svg+xml;utf8,${station.markerIcon}`} />
                            ) : (
                                <img style={{ width: '36px', height: '42px' }} src={station.markerIcon} />
                            )
                            :
                            <img style={{ width: '36px', height: '42px' }} src={getSiteUrl(STATION_NAME_ASSOCIATION[station.typeName])} />
                    }
                </Grid>
                <Grid container size={'grow'}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                        [ {station.code} ] {station.name} - {station.city ? `${station.city}` : ''}
                    </Typography>
                    {
                        station.indicatorDate ? (
                            <Grid container size={12}>
                                <Stack direction='row' spacing={1}>
                                    <Typography sx={{ fontSize: '14px' }}>{`${i18n.date}: `}</Typography>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{getFullDateMini(station.indicatorDate)}</Typography>
                                </Stack>
                                {hasValue(valueLabel) && station.indicatorValue &&
                                    <Stack direction='row' spacing={1}>
                                        <Typography sx={{ fontSize: '14px' }}>{`${valueLabel}: `}</Typography>
                                        <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{round(station.indicatorValue, 3)}</Typography>
                                    </Stack>
                                }
                                {hasValue(station.thresholdValue) &&
                                    <Stack direction='row' spacing={1}>
                                        <Typography sx={{ fontSize: '14px' }}>{`${i18n.threshold}: `}</Typography>
                                        <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{round(station.thresholdValue, 3)}</Typography>
                                    </Stack>
                                }
                                {hasValue(station.stabilityMeasureValue) &&
                                    <Tooltip title={stabilityTooltipContent}>
                                        <Stack direction='row' spacing={1}>
                                            <Typography sx={{ fontSize: '14px' }}>{`${i18n.stability}:`}</Typography>
                                            <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{round(station.stabilityMeasureValue, 3)}</Typography>
                                            <Help
                                                sx={{
                                                    fontSize: 14,
                                                    // position: 'relative',
                                                }}
                                            />
                                        </Stack>
                                    </Tooltip>
                                }
                                {hasValue(station.stabilityMeasureValue) &&
                                    <Tooltip title={stabilityTooltipContent}>
                                        <Stack direction='row' spacing={1}>
                                            <Typography sx={{ fontSize: '14px' }}>{`${i18n.error}:`}</Typography>
                                            <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{getI18nOrLabel(station.message)}</Typography>
                                        </Stack>
                                    </Tooltip>
                                }
                            </Grid>
                        ) : <Typography sx={{ fontSize: '14px', fontWright: 'bold' }}>{i18n.noData}</Typography>
                    }
                </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent={'space-between'} padding={0.2} sx={{ backgroundColor: '#eeeeee' }}>
                <Grid container alignItems={'center'} justifyContent={'flex-start'} spacing={1}>
                    {
                        hasValue(station.jobExecutionId) && (
                            <Tooltip title={i18n.openData}>
                                <Wifi />
                            </Tooltip>
                        )
                    }
                    {
                        station.bookmarked && (
                            <Tooltip title={i18n.bookmark}>
                                <Grade />
                            </Tooltip>
                        )
                    }
                    {template(i18n.calculAtformeasure)({ date: getFullDateMini(station.simulationDate) })}
                </Grid>
                <Grid container alignItems={'center'} justifyContent={'flex-end'} spacing={1}>
                    {
                        (station.currentComments || station.currentMonitoring) && (
                            <Tooltip title={i18n.currentEvents}>
                                <Event onClick={() => onClick(station, EVENTS, 'date')}/>
                            </Tooltip>
                        )
                    }
                    {
                        hasValue(station.currentMonitoring) && (
                            <Tooltip title={i18n.oversight}>
                                <Notifications onClick={() => onClick(station, EVENTS, 'eventType')} />
                            </Tooltip>
                        )
                    }
                    {
                        hasValue(station.currentCampaign) && (
                            <Tooltip title={i18n.currentCampaign}>
                                <NaturePeople onClick={() => onClick(station, CAMPAIGNS)} />
                            </Tooltip>
                        )
                    }
                    <Tooltip title={i18n.description}>
                        <Info onClick={() => onClick(station, DESCRIPTION)} />
                    </Tooltip>
                </Grid>
            </Grid>
        </Stack>
    )
}

StationMapSituationCard.propTypes = {
    onClick: PropTypes.func,
    station: PropTypes.instanceOf(DtoStation),
    onHover: PropTypes.func,
    stabilityPercent: PropTypes.number,
    valueLabel: PropTypes.string,
}

export default StationMapSituationCard
